import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faLayerGroup,
  faPowerOff,
} from "@fortawesome/free-solid-svg-icons";
import { getAssetStatusSummary } from "../../../Apis/DashboardApis/primaryDashboardApi";
import CardLoader from "../../../Common/ShimmerLoader/CardLoader";
import DashboardSectionCard from "./shared/DashboardSectionCard";

const formatCount = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return Number(value).toLocaleString();
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

// Share of total, 0–1 (drives the mini visuals only)
const getShare = (part, total) => (total > 0 ? Math.min(part / total, 1) : 0);

const buildStats = (summary) => {
  const total = toNumber(summary?.totalAssets);
  const active = toNumber(summary?.activeAssets);
  const inactive = toNumber(summary?.inActiveAssets);

  return [
    {
      icon: faLayerGroup,
      label: "Total Assets",
      value: formatCount(summary?.totalAssets),
      color: "#2563eb",
      variant: "bars",
      featured: true,
    },
    {
      icon: faCircleCheck,
      label: "Active",
      value: formatCount(summary?.activeAssets),
      color: "#16a34a",
      variant: "arc",
      share: getShare(active, total),
    },
    {
      icon: faPowerOff,
      label: "Inactive",
      value: formatCount(summary?.inActiveAssets),
      color: "#dc2626",
      variant: "arc",
      share: getShare(inactive, total),
    },
  ];
};

const ARC_LENGTH = 62.83; // half-circle path length for r = 20

// Decorative five-bar motif for the Total Assets card (not data-driven)
const BAR_HEIGHTS = [0.62, 0.45, 0.72, 0.86, 0.3];

const AssetVisual = ({ variant, share = 0 }) => {
  if (variant === "bars") {
    return (
      <span className="pd-asset-bars" aria-hidden="true">
        {BAR_HEIGHTS.map((value, index) => (
          <i key={index}>
            <b style={{ height: `${Math.round(value * 100)}%` }} />
          </i>
        ))}
      </span>
    );
  }

  if (variant === "arc") {
    return (
      <svg className="pd-asset-arc" viewBox="0 0 48 28" aria-hidden="true">
        <path className="pd-asset-arc-track" d="M4 24 A20 20 0 0 1 44 24" />
        <path
          className="pd-asset-arc-fill"
          d="M4 24 A20 20 0 0 1 44 24"
          style={{ strokeDasharray: `${share * ARC_LENGTH} ${ARC_LENGTH}` }}
        />
      </svg>
    );
  }

  return null;
};

const AssetCard = ({ icon, label, value, color, featured, hideIcon, ...visual }) => (
  <div
    className={`pd-asset-card${featured ? " pd-asset-card--featured" : ""}`}
    style={{ "--pd-accent": color }}
  >
    {!featured && !hideIcon ? (
      <span className="pd-asset-icon">
        <FontAwesomeIcon icon={icon || faLayerGroup} />
      </span>
    ) : null}
    <div className="pd-asset-text">
      <span className="pd-asset-label">{label}</span>
      <span className="pd-asset-value">{value}</span>
    </div>
    <AssetVisual {...visual} />
  </div>
);

const AssetStatusSummary = ({ data }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(!data);

  useEffect(() => {
    if (data) {
      return undefined;
    }

    let isMounted = true;

    const fetchSummary = async () => {
      try {
        setLoading(true);
        const response = await getAssetStatusSummary();

        if (isMounted && response?.status && response?.data) {
          setSummary(response.data);
        }
      } catch (error) {
        console.error("Failed to load asset status summary:", error);
        if (isMounted) {
          setSummary(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSummary();

    return () => {
      isMounted = false;
    };
  }, [data]);

  const stats = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }

    return buildStats(summary);
  }, [data, summary]);

  return (
    <DashboardSectionCard
      title="Asset Status Summary"
      className="pd-asset-section"
    >
      {loading ? (
        <CardLoader count={3} />
      ) : (
        <div className="pd-metric-row cols-3 pd-asset-row">
          {stats.map((item) => (
            <AssetCard key={item.label} {...item} />
          ))}
        </div>
      )}
    </DashboardSectionCard>
  );
};

export default AssetStatusSummary;
